import { Check, Building2 } from 'lucide-react';
import { useSiteConfig, useSites, useAuthStore, useTranslation } from '@bms/common';
import type { SiteInfo } from '@bms/common';
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuItem,
  SidebarMenuButton,
  SidebarRail,
} from '@/components/ui/sidebar';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { useTheme } from '@/providers/ThemeProvider';
import { NavLinks } from './NavLinks';
import { NavUser } from './NavUser';
import { resolveSiteName } from './siteName';

// ============================================================================
// AppSidebar — shadcn sidebar-icon-example pattern
// Site switcher header, grouped nav, user footer.
// ============================================================================

export function AppSidebar(props: React.ComponentProps<typeof Sidebar>) {
  const { t } = useTranslation();
  const { data: siteConfig } = useSiteConfig();
  const { data: sites = [] } = useSites();
  const currentSiteCode = useAuthStore((s) => s.currentSiteCode);
  const setSite = useAuthStore((s) => s.setSite);
  const { mode, branding } = useTheme();

  const siteName = resolveSiteName(siteConfig?.siteName, sites, currentSiteCode);
  const rawSettings = siteConfig?.settings;
  let parsedSettings: Record<string, unknown> = {};
  if (rawSettings && typeof rawSettings === 'object') {
    parsedSettings = rawSettings as Record<string, unknown>;
  } else if (typeof rawSettings === 'string') {
    try {
      const parsed = JSON.parse(rawSettings) as unknown;
      if (parsed && typeof parsed === 'object') {
        parsedSettings = parsed as Record<string, unknown>;
      }
    } catch {
      parsedSettings = {};
    }
  }

  const siteDescriptionValue =
    typeof parsedSettings.siteDescription === 'string'
      ? parsedSettings.siteDescription
      : typeof parsedSettings.site_description === 'string'
        ? parsedSettings.site_description
        : '';
  const siteDescription = siteDescriptionValue.trim();
  const hasSiteDescription = siteDescription.length > 0;
  const logoSrc = branding.logoUrl || (mode === 'dark' ? '/logo-dark.svg' : '/logo-light.svg');

  const handleSiteSwitch = (site: SiteInfo) => {
    setSite(site.siteCode);
    // Reload to reinitialize API client with new site context
    window.location.reload();
  };

  return (
    <Sidebar collapsible="icon" {...props}>
      {/* Site switcher header — matching shadcn sidebar-icon-example */}
      <SidebarHeader>
        <SidebarMenu>
          <SidebarMenuItem>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <SidebarMenuButton
                  size="lg"
                  className="data-[state=open]:bg-sidebar-accent data-[state=open]:text-sidebar-accent-foreground"
                >
                  {/* Card surface, not the page surface: in light mode the
                      sidebar is white and --background is a step darker, so a
                      bg-background box read as a grey hole in the header. */}
                  <div className="flex aspect-square size-8 items-center justify-center overflow-hidden rounded-lg border bg-card">
                    <img
                      src={logoSrc}
                      alt={t('shell.brandAlt')}
                      className="h-6 w-6 object-contain"
                    />
                  </div>
                  <div
                    className={`${hasSiteDescription ? 'grid leading-tight' : 'flex items-center'} flex-1 text-left text-ui transition-[opacity,margin] duration-(--duration-base) ease-linear group-data-[collapsible=icon]:-ml-2 group-data-[collapsible=icon]:opacity-0`}
                  >
                    <span className="truncate font-semibold">{siteName}</span>
                    {hasSiteDescription && (
                      <span className="truncate text-caption text-muted-foreground group-data-[collapsible=icon]:hidden">
                        {siteDescription}
                      </span>
                    )}
                  </div>
                </SidebarMenuButton>
              </DropdownMenuTrigger>
              <DropdownMenuContent
                className="w-[--radix-dropdown-menu-trigger-width] min-w-56 rounded-lg"
                align="start"
                side="bottom"
                sideOffset={4}
              >
                <DropdownMenuLabel className="text-caption text-muted-foreground">
                  {t('shell.sites')}
                </DropdownMenuLabel>
                {sites.map((site) => {
                  const isActive = site.siteCode === (currentSiteCode || siteConfig?.siteCode);
                  return (
                    <DropdownMenuItem
                      key={site.siteCode}
                      className="gap-2 p-2"
                      onClick={() => handleSiteSwitch(site)}
                    >
                      <div className="flex size-6 items-center justify-center rounded-md border bg-card">
                        <Building2 className="size-4 shrink-0" />
                      </div>
                      <span className="flex-1 truncate">{site.siteName}</span>
                      {isActive && <Check className="ml-auto size-4" />}
                    </DropdownMenuItem>
                  );
                })}
                {sites.length === 0 && (
                  <DropdownMenuItem className="gap-2 p-2" disabled>
                    <div className="flex size-6 items-center justify-center rounded-md border bg-card">
                      <Building2 className="size-4 shrink-0" />
                    </div>
                    <span className="truncate">{siteName}</span>
                    <Check className="ml-auto size-4" />
                  </DropdownMenuItem>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarHeader>

      {/* Navigation links. Today's stays are the Desk's tiles and queue, not a
          sidebar column (plan-receptionistDesk A2). */}
      <SidebarContent>
        <NavLinks />
      </SidebarContent>

      {/* User dropdown */}
      <SidebarFooter>
        <NavUser />
      </SidebarFooter>

      {/* Drag rail for resize / hover expand */}
      <SidebarRail />
    </Sidebar>
  );
}
